import type { CaixaDaFolha, DocumentoAcademico, ElementoDocumento, SecaoNumerada } from '@miolos/core';
import {
  ESPACO_APOS_TITULO_MM,
  MARGEM_DA_ANOTACAO_MM,
  ROTULO_TIPO_DE_TRABALHO,
  destaqueDaSecao,
  espacoDeUmaLinhaMm,
  formatoDeParagrafo,
  formatoDoCapitulo,
  folhasDeAnotacao,
  geometriaDoDocumento,
  geometriaDoPapel,
  molduraDaFolha,
  numerarSecoes,
  papelDoDocumento,
  pautaDaAnotacao,
  posicaoDaLegenda,
  degrauDeRecuo,
  recuoDeBlocoMm,
  linhaDaReferencia,
  listaDaNorma,
  normaDoDocumento,
  resumoComPalavras,
  rotuloDoCapitulo,
  temConteudoImpresso,
} from '@miolos/core';
import {
  blocosDaSecao,
  blocosDoDocumento,
  larguraDaImagem,
  numerosDasFiguras,
  numerosDasNotas,
  posicaoDaAnotacao,
  posicaoDasNotas,
  tabelaRetangular,
} from '@miolos/core';
import type { BlocoDeTexto } from '@miolos/core';
import { Fragment } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import { estiloDaFaixaEmCss, familiaDoDocumento } from './folha';
import { TextoEditavel } from './TextoEditavel';
import type { MudancaDeTexto } from './TextoEditavel';
import { comMarcas } from './marcas';

/**
 * As ações que o editor manda para dentro do desenho.
 *
 * Sem `aoEditarTexto` o papel é só leitura (é o que a impressão usa). As outras três
 * existem porque um editor de textos precisa saber **onde o cursor está** (a barra de
 * ferramentas age no bloco focado), **onde pôr o cursor** quando cria ou apaga um
 * parágrafo, e **onde ele estava** quando o foco saiu — é assim que a citação
 * autor-data entra no meio da frase e não no começo do parágrafo.
 */
/** O bloco editável que deve receber o foco, e em que ponta dele o cursor cai. */
type FocoPedido = MudancaDeTexto & { posicao: 'inicio' | 'fim' };

interface AcoesDoTexto {
  aoEditarTexto?: (mudanca: MudancaDeTexto, novo: string) => void;
  aoFocarTexto?: (mudanca: MudancaDeTexto) => void;
  focoPedido?: FocoPedido | null;
  /** Onde o cursor está dentro do bloco focado — é onde a citação vai entrar. */
  aoMoverCursor?: (
    mudanca: MudancaDeTexto,
    posicao: { inicio: number; fim: number },
    no: HTMLElement,
  ) => void;
}

/** O foco pedido é para este bloco? */
function mesmaMudanca(pedido: FocoPedido | null | undefined, mudanca: MudancaDeTexto): boolean {
  if (!pedido || pedido.qual !== mudanca.qual || pedido.secaoId !== mudanca.secaoId) return false;
  if (pedido.qual === 'BLOCO' && mudanca.qual === 'BLOCO') return pedido.blocoId === mudanca.blocoId;
  if (pedido.qual === 'ITEM' && mudanca.qual === 'ITEM')
    return pedido.blocoId === mudanca.blocoId && pedido.indice === mudanca.indice;
  return true;
}

/** As propriedades do bloco editável para um pedaço do documento. */
function acoesDoBloco(mudanca: MudancaDeTexto, acoes: AcoesDoTexto) {
  return {
    editavel: Boolean(acoes.aoEditarTexto),
    /*
     * O elemento vai junto com o foco: é por ele que o editor devolve o cursor
     * depois (o campo de página da citação rouba o foco do texto, e a seleção se
     * perde junto — ver a barra de ferramentas).
     */
    aoFocar: acoes.aoFocarTexto ? () => acoes.aoFocarTexto?.(mudanca) : undefined,
    aoMoverCursor: acoes.aoMoverCursor
      ? (posicao: { inicio: number; fim: number }, no: HTMLElement) =>
          acoes.aoMoverCursor?.(mudanca, posicao, no)
      : undefined,
    deveFocar: mesmaMudanca(acoes.focoPedido, mudanca) ? acoes.focoPedido!.posicao : (false as const),
    aoGravar: (novo: string) => acoes.aoEditarTexto?.(mudanca, novo),
  };
}

interface Props {
  documento: DocumentoAcademico;
  /** Resolve a URL da imagem enviada (uploads da API). */
  resolverImagem?: (url: string) => string;
  /** Recuo lateral extra, para a prévia no celular. */
  className?: string;
  /**
   * Quando existe, **o texto do papel é editável**: a pessoa escreve no próprio
   * parágrafo, no título e na citação. Sem isto o desenho é só leitura — é o que a
   * impressão usa, e é o que mantém o papel imprimível.
   */
  aoEditarTexto?: (mudanca: MudancaDeTexto, novo: string) => void;
  /** O bloco que recebeu o foco (a barra de ferramentas age sobre ele). */
  aoFocarTexto?: (mudanca: MudancaDeTexto) => void;
  /**
   * O bloco que deve receber o foco agora — e onde o cursor cai nele. É assim que o
   * editor devolve o cursor depois de criar ou apagar um bloco.
   */
  focoPedido?: (MudancaDeTexto & { posicao: 'inicio' | 'fim' }) | null;
  /**
   * Onde o cursor está no bloco editável. O editor guarda a posição e o bloco para
   * inserir texto no meio da frase (a citação autor-data) mesmo depois de o foco
   * sair — é o que faz o campo de página não perder o lugar da escrita.
   */
  aoMoverCursor?: (
    mudanca: MudancaDeTexto,
    posicao: { inicio: number; fim: number },
    no: HTMLElement,
  ) => void;
  /** `Enter` num bloco: criar outro depois deste (ou um item novo, na lista). */
  aoNovoBloco?: (mudanca: MudancaDeTexto) => void;
  /** `Backspace` num bloco vazio: tirar este e devolver o foco ao anterior. */
  aoApagarBloco?: (mudanca: MudancaDeTexto) => void;
  /**
   * A página de cada seção (id → página impressa), medida pelo navegador.
   *
   * Quando existe, o sumário escreve o número da página ao lado de cada título,
   * como a NBR 6024 pede. Quem mede é o editor (a prévia desenha e informa o
   * topo de cada seção); aqui só se escreve o número.
   */
  paginas?: Map<string, number>;
}

/**
 * Os blocos de uma seção, na ordem em que eles estão.
 *
 * Cada tipo tem o seu desenho e o seu gesto:
 *
 * - **parágrafo** — o texto corrido, com o recuo que o documento escolher;
 * - **citação longa** — recuo de 4 cm, corpo menor, espaço simples (NBR 10520);
 * - **lista** — marcador ou número, com **cada item editável no papel**: `Enter`
 *   cria o item seguinte e `Backspace` num item vazio tira o item. É o que faz uma
 *   lista ser uma lista (e não um parágrafo com hífen na frente);
 * - **bloco vazio** só é desenhado no editor, onde ele é o lugar do cursor: no papel
 *   ele não existe, porque um espaço em branco não imprime.
 *
 * O rótulo de cada bloco (o que o leitor de tela lê, e o que o teste procura) traz a
 * posição **entre os do mesmo tipo** — "Parágrafo 2 de Introdução" continua sendo o
 * segundo parágrafo mesmo que haja uma lista antes dele.
 */
/**
 * A lista das notas.
 *
 * O navegador **não sabe imprimir nota no pé da página** (a `float: footnote` do CSS não
 * existe no Chromium). O mais perto do rodapé que se chega é o fim da seção: a nota sai logo
 * abaixo do texto a que pertence, com um filete em cima, em corpo menor e espaço simples — a
 * forma da nota de rodapé. Quem preferir todas juntas no fim do trabalho escolhe a **nota de
 * fim** no painel, e aí a lista sai depois do texto, no bloco "Notas".
 */
function ListaDeNotas({
  itens,
  geometria,
  onde,
}: {
  itens: Array<{ numero: number; texto: string }>;
  geometria: ReturnType<typeof geometriaDoPapel>;
  onde: 'secao' | 'documento';
}) {
  if (itens.length === 0) return null;

  return (
    <section data-notas={onde} style={{ marginTop: '6mm', breakInside: 'avoid' }}>
      {/* O filete de abertura é o que separa a nota do texto, como no pé da página. */}
      <div
        style={{ borderTop: '0.2mm solid #333333', width: '50mm', marginBottom: '2mm' }}
        aria-hidden="true"
      />
      {itens.map((nota) => (
        <p
          key={`${onde}-${nota.numero}`}
          style={{
            fontSize: `${geometria.reduzidoPt}pt`,
            lineHeight: 1,
            textIndent: 0,
            marginBottom: '1.5mm',
            textAlign: 'justify',
          }}
        >
          <sup style={{ fontSize: '0.75em' }}>{nota.numero}</sup> {comMarcas(nota.texto)}
        </p>
      ))}
    </section>
  );
}

function BlocosDaSecao({
  secaoId,
  tituloDaSecao,
  blocos,
  geometria,
  estiloDoParagrafo,
  acoes,
  aoNovoBloco,
  aoApagarBloco,
  numerosDasNotas,
  numerosDasFiguras,
  notasNoFimDaSecao,
  imagem,
  larguraDaManchaMm,
  legendaAbaixo,
}: {
  secaoId: string;
  tituloDaSecao: string;
  blocos: BlocoDeTexto[];
  geometria: ReturnType<typeof geometriaDoPapel>;
  estiloDoParagrafo: CSSProperties;
  acoes: AcoesDoTexto;
  /** O número de cada nota (a conta é do documento inteiro: o mapa vem de cima). */
  numerosDasNotas: Map<string, number>;
  /** O número de cada figura, contado do começo ao fim do documento. */
  numerosDasFiguras: Map<string, number>;
  /** As notas saem no fim **da seção** (a nota de rodapé) — ou todas no fim do documento. */
  notasNoFimDaSecao: boolean;
  /** O endereço de verdade da imagem (`/uploads/...`). */
  imagem: (url: string) => string;
  /** A largura da mancha, que é o teto da figura. */
  larguraDaManchaMm: number;
  /** A legenda da figura sai acima (norma) ou abaixo (manual do Centro Paula Souza). */
  legendaAbaixo: boolean;
  aoNovoBloco?: (mudanca: MudancaDeTexto) => void;
  aoApagarBloco?: (mudanca: MudancaDeTexto) => void;
}) {
  const editavel = Boolean(acoes.aoEditarTexto);
  const vazioMm = `${geometria.corpoPt * 1.4}pt`;
  const titulo = tituloDaSecao.trim() || 'seção sem título';

  /*
   * O recuo do bloco (os botões "aumentar/diminuir recuo" do editor), em degraus
   * de 12,5 mm. Zero — o caso de todo documento salvo antes deste recurso — não
   * desloca nada: é o bloco na margem, como sempre saiu impresso.
   */
  const recuoDe = (bloco: BlocoDeTexto) => recuoDeBlocoMm(bloco.recuo, geometria.papel);
  /** O recuo no dado do desenho: é ele que o teste lê na folha impressa. */
  const dadosDoRecuo = (bloco: BlocoDeTexto): Record<string, string> =>
    recuoDe(bloco) > 0 ? { 'data-recuo': String(degrauDeRecuo(bloco.recuo, geometria.papel)) } : {};
  /** Os dados do bloco de texto: o tipo dele e, quando houver, o recuo. */
  const dadosDoBloco = (bloco: BlocoDeTexto, citacao: boolean): Record<string, string> => ({
    ...(citacao ? { 'data-citacao-longa': '' } : { 'data-paragrafo': '' }),
    ...dadosDoRecuo(bloco),
  });

  let paragrafos = 0;
  let citacoes = 0;
  let tabelas = 0;

  /** As notas desta seção, com o número que o documento inteiro lhes deu. */
  const notasDaSecao = blocos
    .filter((bloco) => bloco.tipo === 'NOTA' && bloco.texto?.trim())
    .map((bloco) => ({ numero: numerosDasNotas.get(bloco.id) ?? 0, texto: bloco.texto ?? '' }));

  /** O que `Enter` e `Backspace` fazem neste bloco. */
  const gestos = (mudanca: MudancaDeTexto) => ({
    ...(aoNovoBloco ? { aoNovoParagrafo: () => aoNovoBloco(mudanca) } : {}),
    ...(aoApagarBloco ? { aoApagarVazio: () => aoApagarBloco(mudanca) } : {}),
  });

  return (
    <>
      {blocos.map((bloco) => {
        /*
         * A **nota** no texto é só o número: o texto dela sai na lista (no fim da seção ou no
         * fim do documento), e é o motor que numera — mover uma nota renumera as outras.
         */
        if (bloco.tipo === 'NOTA') {
          const numero = numerosDasNotas.get(bloco.id) ?? 0;
          return (
            <sup
              key={bloco.id}
              data-nota=""
              title={bloco.texto ?? ''}
              style={{ fontSize: '0.75em', verticalAlign: 'super' }}
            >
              {numero}
            </sup>
          );
        }

        /*
         * A **figura** no meio do texto: legenda, imagem e fonte.
         *
         * A NBR 14724 põe a legenda **acima** da ilustração; o manual do Centro Paula
         * Souza põe o título **abaixo**. O que muda é a ordem dos três pedaços — o
         * número da figura e a fonte andam com a legenda, seja qual for o lado.
         */
        if (bloco.tipo === 'IMAGEM') {
          /* O número vem do documento inteiro: a figura do texto e a da lista avulsa contam juntas. */
          const numeroDaFigura = numerosDasFiguras.get(bloco.id) ?? 0;
          const url = (bloco.url ?? '').trim();

          const legenda = (
            <figcaption
              style={{
                fontSize: `${geometria.reduzidoPt}pt`,
                lineHeight: 1,
                marginTop: legendaAbaixo ? '2mm' : 0,
                marginBottom: legendaAbaixo ? 0 : '2mm',
              }}
            >
              Figura {numeroDaFigura}
              {bloco.legenda?.trim() ? ' — ' : ''}
              {comMarcas(bloco.legenda?.trim() ?? '')}
            </figcaption>
          );

          const desenho = url ? (
            <img
              src={imagem(url)}
              alt={bloco.legenda?.trim() ?? 'figura'}
              style={{ width: `${larguraDaImagem(bloco, larguraDaManchaMm)}mm`, maxWidth: '100%' }}
            />
          ) : (
            /* Sem arquivo a moldura fica no lugar da imagem: o espaço já está reservado. */
            <div
              style={{
                width: `${larguraDaImagem(bloco, larguraDaManchaMm)}mm`,
                maxWidth: '100%',
                height: '50mm',
                margin: '0 auto',
                border: '0.2mm solid #999999',
              }}
            />
          );

          const fonte = bloco.fonte?.trim() ? (
            <p
              style={{
                fontSize: `${geometria.reduzidoPt}pt`,
                lineHeight: 1,
                marginTop: legendaAbaixo ? '1mm' : '2mm',
                textIndent: 0,
                textAlign: 'left',
              }}
            >
              Fonte: {comMarcas(bloco.fonte.trim())}
            </p>
          ) : null;

          return (
            <figure
              key={bloco.id}
              data-figura=""
              style={{ margin: '8mm 0', breakInside: 'avoid', textAlign: 'center' }}
            >
              {legendaAbaixo ? (
                <>
                  {desenho}
                  {legenda}
                  {fonte}
                </>
              ) : (
                <>
                  {legenda}
                  {desenho}
                  {fonte}
                </>
              )}
            </figure>
          );
        }

        if (bloco.tipo === 'TABELA') {
          tabelas += 1;
          const linhas = tabelaRetangular(bloco.linhas ?? []);
          const [cabecalho, ...corpo] = linhas;
          return (
            <figure
              key={bloco.id}
              data-tabela=""
              style={{ margin: '8mm 0', breakInside: 'avoid' }}
            >
              {/*
                A legenda vem **acima** do quadro, como a norma pede, e o número é do
                motor (a ordem das tabelas no documento) — quem escreve escreve a
                legenda, não o rótulo.
              */}
              <figcaption style={{ fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, marginBottom: '2mm' }}>
                Tabela {tabelas}
                {bloco.legenda?.trim() ? ' — ' : ''}
                {comMarcas(bloco.legenda?.trim() ?? '')}
              </figcaption>

              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: `${geometria.reduzidoPt}pt`,
                  lineHeight: 1.2,
                }}
              >
                {cabecalho ? (
                  <thead>
                    <tr>
                      {cabecalho.map((celula, coluna) => (
                        <th
                          key={coluna}
                          style={{
                            borderTop: '0.3mm solid #000000',
                            borderBottom: '0.3mm solid #000000',
                            padding: '1.2mm 1.5mm',
                            textAlign: 'left',
                            fontWeight: 700,
                          }}
                        >
                          {comMarcas(celula)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                ) : null}
                <tbody>
                  {corpo.map((linha, indice) => (
                    <tr key={indice}>
                      {linha.map((celula, coluna) => (
                        <td
                          key={coluna}
                          style={{
                            padding: '1.2mm 1.5mm',
                            textAlign: 'left',
                            /* A última linha fecha o quadro: filete no pé. */
                            borderBottom:
                              indice === corpo.length - 1 ? '0.3mm solid #000000' : undefined,
                          }}
                        >
                          {comMarcas(celula)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {bloco.fonte?.trim() ? (
                <figcaption
                  style={{ fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, marginTop: '2mm' }}
                >
                  Fonte: {comMarcas(bloco.fonte.trim())}
                </figcaption>
              ) : null}
            </figure>
          );
        }

        if (bloco.tipo === 'LISTA') {
          const Lista = (bloco.lista === 'NUMERADA' ? 'ol' : 'ul') as 'ul';
          const recuoMm = recuoDe(bloco);
          return (
            <Lista
              key={bloco.id}
              data-lista={bloco.lista === 'NUMERADA' ? 'NUMERADA' : 'MARCADA'}
              {...dadosDoRecuo(bloco)}
              style={{
                /* O recuo da lista acompanha o do parágrafo: a norma quer o texto na mesma coluna. */
                margin: `0 0 0 ${6 + recuoMm}mm`,
                paddingLeft: '6mm',
              }}
            >
              {(bloco.itens ?? []).map((item, indice) =>
                item.trim() || editavel ? (
                  <TextoEditavel
                    key={`${bloco.id}-${indice}`}
                    como="li"
                    valor={item}
                    rotulo={`Item ${indice + 1} da lista de ${titulo}`}
                    style={{ ...estiloDoParagrafo, ...(item.trim() ? {} : { minHeight: vazioMm }) }}
                    {...acoesDoBloco({ qual: 'ITEM', secaoId, blocoId: bloco.id, indice }, acoes)}
                    {...gestos({ qual: 'ITEM', secaoId, blocoId: bloco.id, indice })}
                  />
                ) : null,
              )}
            </Lista>
          );
        }

        const texto = bloco.texto ?? '';
        if (!texto.trim() && !editavel) return null;

        const citacao = bloco.tipo === 'CITACAO';
        const recuoMm = recuoDe(bloco);
        if (citacao) citacoes += 1;
        else paragrafos += 1;

        const rotulo = citacao
          ? `Citação longa${citacoes > 1 ? ` ${citacoes}` : ''} de ${titulo}`
          : `Parágrafo ${paragrafos} de ${titulo}`;

        return (
          <TextoEditavel
            key={bloco.id}
            valor={texto}
            rotulo={rotulo}
            dados={dadosDoBloco(bloco, citacao)}
            style={{
              ...(citacao
                ? {
                    /* O recuo da citação longa é da norma (4 cm); o degrau vem por cima. */
                    marginLeft: `${geometria.recuoCitacaoMm + recuoMm}mm`,
                    fontSize: `${geometria.reduzidoPt}pt`,
                    lineHeight: 1,
                    marginBottom: '6mm',
                  }
                : {
                    ...estiloDoParagrafo,
                    ...(recuoMm > 0 ? { marginLeft: `${recuoMm}mm` } : {}),
                  }),
              ...(texto.trim() ? {} : { minHeight: vazioMm }),
            }}
            {...acoesDoBloco({ qual: 'BLOCO', secaoId, blocoId: bloco.id }, acoes)}
            {...gestos({ qual: 'BLOCO', secaoId, blocoId: bloco.id })}
          />
        );
      })}

      {/* As notas desta seção, no fim dela — é a nota de rodapé que o navegador imprime. */}
      {notasNoFimDaSecao ? (
        <ListaDeNotas itens={notasDaSecao} geometria={geometria} onde="secao" />
      ) : null}
    </>
  );
}

/**
 * O `id` da seção no HTML — o alvo dos links do sumário.
 *
 * O prefixo separa o `id` do documento do resto da página (e o `id` da seção vem
de um documento que já viveu em versões diferentes do gerador).
 */
function idDaSecao(id: string): string {
  return `secao-${id}`;
}

/**
 * O clique numa linha do sumário cai na seção — como no Word.
 *
 * O `href` continua no desenho (é ele que o PDF transforma em link interno, e
 * clicar de verdade leva ao mesmo lugar), mas na tela quem rola é este código: o
 * `#` no endereço brigaria com a rota do site. E a busca tem de ser **dentro desta
 * cópia** do documento — a página tem duas (a prévia e o portal de impressão), e
 * `getElementById` acharia sempre a primeira.
 */
/** O primeiro lugar, acima deste nó, que rola de verdade (a caixa da prévia). */
function roladorAcimaDe(no: Element | null): HTMLElement | null {
  for (let atual = no?.parentElement ?? null; atual; atual = atual.parentElement) {
    const estilo = getComputedStyle(atual);
    if (/(auto|scroll)/.test(estilo.overflowY) && atual.scrollHeight > atual.clientHeight) {
      return atual;
    }
  }
  return null;
}

function irParaOSecao(evento: MouseEvent<HTMLAnchorElement>, destino: string): void {
  evento.preventDefault();
  const raiz = evento.currentTarget.closest('.documento-abnt');
  const alvo = raiz?.querySelector<HTMLElement>(`[id="${destino}"]`);
  if (!alvo) return;

  /*
   * A rolagem é feita **por conta própria**, e não com `scrollIntoView`.
   *
   * A prévia é encolhida por `zoom` (o papel A4 tem 794 px de largura e precisa caber
   * na coluna), e o `scrollIntoView` do navegador erra a conta quando há `zoom` no
   * caminho: a linha clicada parava 265 px abaixo do alto da prévia. A conta daqui é
   * pelas caixas reais (`getBoundingClientRect`), que já vêm na medida do desenho —
   * então ela vale com qualquer fator de encolhimento.
   */
  const rolador = roladorAcimaDe(raiz);
  const deslocamento = alvo.getBoundingClientRect().top - (rolador ?? raiz!).getBoundingClientRect().top;

  if (rolador) rolador.scrollTo({ top: rolador.scrollTop + deslocamento, behavior: 'smooth' });
  else alvo.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

/**
 * O documento acadêmico desenhado como a ABNT pede.
 *
 * **Por que HTML e não páginas em milímetros:** num trabalho acadêmico o texto
 * **corre** — quem pagina é o navegador, que sabe quebrar linha, evitar viúva e
 * separar parágrafo. O motor do miolo de agenda faz o contrário (calcula a altura
 * de cada página), e é por isso que são dois desenhos diferentes: ali o conteúdo
 * é decoração em caixas, aqui é texto.
 *
 * O que o motor decide, e este arquivo só obedece: margens, corpo e entrelinha,
 * recuo de parágrafo, citação longa, numeração progressiva, ordem e formato das
 * referências. Está tudo em `packages/core/src/documento/abnt.ts`, com teste.
 *
 * Uma folha contínua de 210 mm com as margens da norma — `break-before: page`
 * marca o que começa em folha nova (capa, folha de rosto, resumo, sumário,
 * referências...). Na tela as quebras aparecem como linha tracejada, para a
 * pessoa ver onde o papel vai virar.
 */
export function DocumentoImpresso({
  documento,
  resolverImagem,
  className,
  aoEditarTexto,
  aoFocarTexto,
  aoMoverCursor,
  focoPedido,
  aoNovoBloco,
  aoApagarBloco,
  paginas,
}: Props) {
  const imagem = (url: string | null | undefined): string => {
    if (!url) return '';
    return resolverImagem ? resolverImagem(url) : url;
  };

  const { identificacao, itens } = documento;
  const tem = (item: ElementoDocumento) => itens.includes(item);

  /*
   * O papel decide tudo o que é medida: folha, margens, corpo do texto, recuos.
   * O livro pode sair em A5 (`geometriaDoPapel`); o trabalho acadêmico é A4 por
   * norma — pedir A5 nele não muda nada, por decisão do motor.
   */
  const papel = papelDoDocumento(documento);
  /*
   * A geometria do documento: o papel decide tudo, e por cima vem o corpo que a
   * pessoa escolheu (livro em A5 com o texto maior, por exemplo).
   */
  const geometria = geometriaDoDocumento(documento);
  /** A legenda da figura abaixo da imagem é do manual do Centro Paula Souza. */
  const legendaAbaixo = posicaoDaLegenda(documento) === 'ABAIXO';
  const comCapitulos = documento.modelo === 'LIVRO';

  /*
   * A folha (cabeçalho, rodapé, número) e a abertura do capítulo vêm resolvidas
   * do motor. Aqui elas só viram desenho — e a folha aparece de dois jeitos: no
   * papel, pelas caixas de margem que o editor injeta no `@page`; na tela, por
   * uma faixa cinza no alto e no pé, para a pessoa ver o que vai sair.
   */
  const moldura = molduraDaFolha(documento);
  const formato = formatoDoCapitulo(documento);
  /*
   * O corpo do título do capítulo: o corpo do texto mais o aumento do formato —
   * a menos que a pessoa tenha escolhido um tamanho próprio (livro em A5).
   */
  const corpoDoTituloPt = documento.tamanhoDoTituloPt ?? geometria.corpoPt + formato.aumentoPt;
  const pauta = pautaDaAnotacao(geometria, documento.estiloDeAnotacao ?? 'LINHAS');
  const quantasAnotacoes = folhasDeAnotacao(documento);

  /*
   * O parágrafo: **recuado** (1,25 cm na primeira linha, sem linha em branco entre um
   * e outro) ou **simples** (na margem, com uma linha em branco separando). A norma
   * aceita os dois — o que ela não aceita é os dois ao mesmo tempo, que é o que sai de
   * um texto colado do Word. O espaço da linha é calculado do corpo e da entrelinha do
   * papel (`espacoDeUmaLinhaMm`), e não escolhido a dedo.
   */
  const paragrafo = formatoDeParagrafo(documento);
  const umaLinhaMm = espacoDeUmaLinhaMm(geometria);
  const estiloDoParagrafo: CSSProperties = {
    textIndent: paragrafo === 'SIMPLES' ? 0 : undefined,
    marginBottom: paragrafo === 'SIMPLES' ? `${umaLinhaMm}mm` : 0,
  };

  /*
   * O que o **editor** precisa de cada bloco de texto: gravar, avisar o foco e
   * receber o foco quando o editor pede (depois de criar ou apagar um parágrafo).
   * Reunido num objeto só porque ele atravessa três lugares: parágrafo, citação e
   * título.
   */
  const acoes: AcoesDoTexto = { aoEditarTexto, aoFocarTexto, aoMoverCursor, focoPedido };

  /*
   * Seção **sem conteúdo** não imprime: um título solto não é um capítulo.
   *
   * O que é "com conteúdo" é do motor (`temConteudoImpresso`): o texto da seção
   * **ou** o de uma subseção dela — um capítulo cujo texto está todo nas partes de
   * dentro tem o que imprimir, e é o título dele que abre o capítulo. O que decide
   * isso são os **blocos**, e não o espelho dos parágrafos: uma seção que só tem
   * lista (ou tabela) tem `paragrafos` vazio, e olhar só para o espelho a esconderia.
   */
  const secoes = numerarSecoes(documento.secoes, comCapitulos ? { livro: true } : {}).filter((_, indice) =>
    temConteudoImpresso(documento.secoes, indice),
  );
  /*
   * Quebra de folha: **só a seção primária** (o título 1) abre folha nova — é o que a
   * norma permite e o que o modelo pede (e não o artigo, que é texto corrido).
   * Subseção nunca começa em folha nova: ela corre com o texto, com uma linha antes e
   * uma depois. Uma subseção marcada à mão para abrir folha nova (documento antigo)
   * também sai corrida — a hierarquia da norma vale mais que a marcação antiga, e o
   * texto não fica com buraco branco no meio.
   *
   * A conta é feita **antes do desenho** porque quem põe as folhas de anotação antes
   * do capítulo precisa saber se ele já abre folha sozinho (duas quebras seguidas
   * dariam uma folha em branco).
   */
  const abreFolhaNova = secoes.map(
    (item) => item.nivel === 1 && Boolean(item.secao.novaPagina || moldura.capituloEmFolhaNova),
  );

  /*
   * Onde as folhas de anotação entram.
   *
   * `ENTRE_CAPITULOS` (o padrão) repete o jogo de folhas antes de cada capítulo — o
   * primeiro não conta. Quando o documento ainda não tem **dois** capítulos, elas saem
   * no fim: é o único lugar possível, e folha ligada que não imprime seria o defeito.
   */
  const anotacoesLigadas = tem('PAGINA_DE_ANOTACAO') && quantasAnotacoes > 0;
  const capitulos = secoes.filter((item) => item.nivel === 1);
  const primeiroCapitulo = capitulos[0]?.secao.id;
  const anotacoesEntreCapitulos =
    anotacoesLigadas && posicaoDaAnotacao(documento) === 'ENTRE_CAPITULOS' && capitulos.length > 1;
  const anotacoesNoFim = anotacoesLigadas && !anotacoesEntreCapitulos;

  /** Este capítulo leva as folhas de anotação antes dele? */
  const folhasAntesDe = (indice: number): boolean =>
    anotacoesEntreCapitulos &&
    secoes[indice]!.nivel === 1 &&
    secoes[indice]!.secao.id !== primeiroCapitulo;

  const norma = normaDoDocumento(documento);
  /* A lista na ordem da norma, com o número de cada obra (as numeradas o imprimem). */
  const referencias = listaDaNorma(documento.referencias, norma);

  /*
   * As **notas**: o número de cada uma (a conta é do documento inteiro, na ordem em que elas
   * aparecem) e onde a lista sai. `numerosDasNotas` devolve o mapa pelo id do bloco, que é o
   * que o desenho tem à mão.
   */
  const mapaDasNotas = numerosDasNotas(documento);
  /*
   * As **figuras** contam do começo ao fim do documento (a da primeira seção é a
   * "Figura 1", a da última é a "Figura N") — e a lista avulsa entra na mesma
   * contagem, na ordem em que é desenhada depois do texto.
   */
  const mapaDasFiguras = numerosDasFiguras(documento);
  const notasNoFimDaSecao = posicaoDasNotas(documento) === 'FIM_DA_SECAO';
  const todasAsNotas = notasNoFimDaSecao
    ? []
    : blocosDoDocumento(documento)
        .filter((bloco) => bloco.tipo === 'NOTA' && bloco.texto?.trim())
        .map((bloco) => ({ numero: mapaDasNotas.get(bloco.id) ?? 0, texto: bloco.texto ?? '' }));

  /** A largura da mancha: o teto de qualquer figura. */
  const larguraDaManchaMm = Math.max(
    40,
    geometria.larguraMm - geometria.margens.esquerda - geometria.margens.direita,
  );
  const autores = identificacao.autores.map((autor) => autor.nome.trim()).filter(Boolean);
  const tituloCompleto = [identificacao.titulo, identificacao.subtitulo].filter(Boolean).join(': ');
  const tipo = identificacao.tipo ? ROTULO_TIPO_DE_TRABALHO[identificacao.tipo] : 'Trabalho';

  return (
    <div
      className={['documento-abnt', className].filter(Boolean).join(' ')}
      style={{
        width: `${geometria.larguraMm}mm`,
        background: '#ffffff',
        color: '#000000',
        fontFamily: familiaDoDocumento(documento),
        fontSize: `${geometria.corpoPt}pt`,
        lineHeight: geometria.entrelinha,
        padding: `${geometria.margens.topo}mm ${geometria.margens.direita}mm ${geometria.margens.fundo}mm ${geometria.margens.esquerda}mm`,
      }}
      data-modelo={documento.modelo}
      data-papel={papel}
      data-capitular={documento.capitular ? 'true' : undefined}
    >
      <style>{`
        .documento-abnt p { margin: 0 0 0 0; text-align: justify; text-indent: ${geometria.recuoParagrafoMm}mm; orphans: 2; widows: 2; }
        .documento-abnt h1, .documento-abnt h2, .documento-abnt h3,
        .documento-abnt h4, .documento-abnt h5, .documento-abnt h6 { font-weight: 700; text-align: left; text-indent: 0; break-after: avoid; }
        .documento-abnt .abnt-titulo { text-align: center; text-indent: 0; }
        .documento-abnt figure { break-inside: avoid; }
        /* Quebra de folha na impressão; linha tracejada na tela (só na prévia). */
        .documento-abnt [data-folha-nova] { break-before: page; page-break-before: always; }
        /* Folha inteira (anotação): ocupa a página exata e não transborda. */
        .documento-abnt [data-folha-inteira] { display: flex; flex-direction: column; overflow: hidden; }
        /*
         * Letra capitulada: a primeira letra do capítulo em corpo grande, com a
         * linha de baixo subindo ao lado dela. Zerar o recuo do parágrafo é o
         * que impede a capitular de sair empurrada para dentro da mancha.
         *
         * O display flow-root é o que **prende a letra flutuante no parágrafo**:
         * sem ele o float escapa e qualquer caixa mais adiante que crie contexto
         * próprio (a folha de anotação, que tem overflow: hidden) encolhe para
         * desviar dele, como se a capitular ainda estivesse ao lado. Custou uma
         * folha de anotação 22 mm mais estreita — medido, não deduzido.
         */
        .documento-abnt[data-capitular="true"] [data-capitulo] > p:first-of-type {
          text-indent: 0;
          display: flow-root;
        }
        .documento-abnt[data-capitular="true"] [data-capitulo] > p:first-of-type::first-letter {
          float: left; font-size: 3.1em; line-height: 0.82; padding-right: 2mm; font-weight: 700;
        }
        .documento-abnt .abnt-quebra { display: none; }
        /* A faixa da folha só existe no papel; na tela é esta tira cinza. */
        .documento-abnt [data-faixa-previa] { display: none; }
        @media screen {
          .documento-abnt .abnt-quebra {
            display: block; margin: 8mm 0; border-top: 0.3mm dashed #c9ccd4;
          }
          /*
           * A folha de anotação é a **folha inteira**: na tela ela fura as margens
           * do texto, para a prévia mostrar a largura que sai no papel (a margem da
           * anotação, não as da norma). No papel quem faz isso é o @page anotacao,
           * que troca a margem da página — sem ele a pauta ficaria presa à mancha e
           * metade da folha sairia vazia.
           */
          .documento-abnt [data-elemento="PAGINA_DE_ANOTACAO"] {
            margin-left: -${geometria.margens.esquerda - MARGEM_DA_ANOTACAO_MM}mm;
            margin-right: -${geometria.margens.direita - MARGEM_DA_ANOTACAO_MM}mm;
          }
          .documento-abnt [data-faixa-previa] {
            display: flex; align-items: center; justify-content: space-between; gap: 2mm;
            line-height: 1.2;
          }
          .documento-abnt [data-faixa-previa="alto"] { margin-bottom: 6mm; padding-bottom: 1.5mm; }
          .documento-abnt [data-faixa-previa="pe"] { margin-top: 8mm; padding-top: 1.5mm; }
          /*
           * O texto no papel é editável: este realce é o que faz a pessoa descobrir
           * isso. Sem ele, ninguém clica num parágrafo para escrever — e a ideia do
           * editor é justamente não ter caixa de texto separada do que vai imprimir.
           */
          .documento-abnt [data-texto-editavel] { border-radius: 1mm; }
          .documento-abnt [data-texto-editavel]:hover {
            background: #f2f6ff; box-shadow: 0 0 0 0.4mm #d3e2ff; cursor: text;
          }
          .documento-abnt [data-texto-editavel]:focus {
            background: #ffffff; box-shadow: 0 0 0 0.4mm var(--cor-alegre-3, #7c9cff); outline: none;
          }
          /* Parágrafo ainda vazio: a dica fica no lugar do texto (e não imprime). */
          .documento-abnt [data-texto-editavel]:empty::before { content: 'Escreva...'; color: #b9bec8; }
        }
        @media print {
          .documento-abnt { padding: 0 !important; width: auto !important; }
          .documento-abnt .abnt-quebra { display: none !important; }
          .documento-abnt [data-faixa-previa] { display: none !important; }
          /* O PDF do anexo não sai no papel: o anexo vai como arquivo, não como página. */
          .documento-abnt [data-anexo-pdf] { display: none !important; }
        }
      `}</style>

      {/*
        A faixa da folha como ela sai no papel — na tela, para a pessoa ver o que
        vai ser impresso: o cabeçalho em cima, o rodapé no fim. No papel quem
        desenha é o `@page` (o editor o injeta na hora de imprimir).

        O traço embaixo da faixa é a pista do filete: cheio quando ele vai sair
        impresso, tracejado quando é só o limite da prévia.
      */}
      {temFaixa(moldura.comum.alto) ? (
        <div
          aria-hidden
          data-faixa-previa="alto"
          style={{
            ...estiloDaFaixaEmCss(documento),
            borderBottom: moldura.filete ? '0.2mm solid #999999' : '0.2mm dashed #c9ccd4',
          }}
        >
          <span>{textoDaCaixa(moldura.comum.alto.esquerda)}</span>
          <span>{textoDaCaixa(moldura.comum.alto.centro)}</span>
          <span>{textoDaCaixa(moldura.comum.alto.direita)}</span>
        </div>
      ) : null}

      {/* ---------------- CAPA ---------------- */}
      {tem('CAPA') ? (
        <section data-folha-nova data-bloco="capa" data-elemento="CAPA" style={{ textAlign: 'center', marginBottom: '20mm' }}>
          {documento.capa ? (
            <img
              src={imagem(documento.capa.url)}
              alt=""
              style={{ width: '100%', height: `${documento.capa.alturaMm}mm`, objectFit: 'contain', marginBottom: '12mm' }}
            />
          ) : null}
          {/*
            A capa do **livro** é o título com o autor: instituição e cidade só
            aparecem se a pessoa preencher (e a de livro não traz o rótulo
            "INSTITUIÇÃO" no lugar). No trabalho acadêmico, instituição, unidade
            e curso são a primeira coisa da capa, como a norma manda.
          */}
          {comCapitulos ? null : (
            <>
              <p className="abnt-titulo" style={{ fontWeight: 700, textTransform: 'uppercase' }}>
                {identificacao.instituicao || 'INSTITUIÇÃO'}
              </p>
              {identificacao.unidade ? <p className="abnt-titulo">{identificacao.unidade}</p> : null}
              {identificacao.curso ? <p className="abnt-titulo">{identificacao.curso}</p> : null}
            </>
          )}

          <p
            className="abnt-titulo"
            style={{ marginTop: comCapitulos ? '0' : '40mm', fontWeight: 700 }}
          >
            {autores.join('; ') || (comCapitulos ? 'NOME DO AUTOR' : 'AUTOR')}
          </p>
          <p
            className="abnt-titulo"
            style={{
              marginTop: comCapitulos ? '24mm' : '18mm',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            {tituloCompleto || (comCapitulos ? 'TÍTULO DO LIVRO' : 'TÍTULO DO TRABALHO')}
          </p>
          <p className="abnt-titulo" style={{ marginTop: comCapitulos ? '50mm' : '40mm' }}>
            {identificacao.cidade || (comCapitulos ? '' : 'CIDADE')}
          </p>
          <p className="abnt-titulo">{identificacao.ano}</p>
        </section>
      ) : null}

      {/* ---------------- FOLHA DE ROSTO ---------------- */}
      {tem('FOLHA_DE_ROSTO') ? (
        <section data-folha-nova data-bloco="rosto" data-elemento="FOLHA_DE_ROSTO" style={{ marginBottom: '20mm' }}>
          <p className="abnt-titulo" style={{ fontWeight: 700 }}>{autores.join('; ') || 'AUTOR'}</p>
          <p className="abnt-titulo" style={{ marginTop: '30mm', fontWeight: 700, textTransform: 'uppercase' }}>
            {tituloCompleto || 'TÍTULO DO TRABALHO'}
          </p>
          <div style={{ marginTop: '20mm', marginLeft: '55mm', textAlign: 'justify' }}>
            <p style={{ textIndent: 0, fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1 }}>
              {tipo} apresentado {identificacao.curso ? `ao curso de ${identificacao.curso}` : ''}{' '}
              {identificacao.instituicao ? `da ${identificacao.instituicao}` : ''} como requisito parcial
              {identificacao.orientador ? `, sob orientação de ${identificacao.orientador}` : ''}.
            </p>
          </div>
          <p className="abnt-titulo" style={{ marginTop: '30mm' }}>{identificacao.cidade || 'CIDADE'}</p>
          <p className="abnt-titulo">{identificacao.ano}</p>
        </section>
      ) : null}

      {/* ---------------- FOLHA DE APROVAÇÃO ---------------- */}
      {tem('FOLHA_DE_APROVACAO') ? (
        <section data-folha-nova data-bloco="aprovacao" data-elemento="FOLHA_DE_APROVACAO" style={{ marginBottom: '20mm' }}>
          <p className="abnt-titulo" style={{ fontWeight: 700 }}>{autores.join('; ') || 'AUTOR'}</p>
          <p className="abnt-titulo" style={{ marginTop: '24mm', fontWeight: 700, textTransform: 'uppercase' }}>
            {tituloCompleto || 'TÍTULO DO TRABALHO'}
          </p>
          <p style={{ marginTop: '24mm', textAlign: 'justify', textIndent: 0 }}>
            Aprovado em: ____ / ____ / ________
          </p>
          {(identificacao.banca ?? '').split('\n').filter(Boolean).map((linha, indice) => (
            <p key={indice} style={{ marginTop: '14mm', textAlign: 'center', textIndent: 0 }}>
              _______________________________________
              <br />
              <span style={{ fontSize: `${geometria.reduzidoPt}pt` }}>{linha}</span>
            </p>
          ))}
        </section>
      ) : null}

      {/* ---------------- DEDICATÓRIA, AGRADECIMENTOS, EPÍGRAFE ---------------- */}
      {tem('DEDICATORIA') && documento.dedicatoria?.trim() ? (
        <section data-folha-nova data-bloco="dedicatoria" data-elemento="DEDICATORIA" style={{ marginBottom: '20mm', textAlign: 'right', marginTop: '60mm' }}>
          <p style={{ textIndent: 0, fontStyle: 'italic' }}>{comMarcas(documento.dedicatoria)}</p>
        </section>
      ) : null}

      {tem('AGRADECIMENTOS') && documento.agradecimentos?.trim() ? (
        <section data-folha-nova data-bloco="agradecimentos" data-elemento="AGRADECIMENTOS" style={{ marginBottom: '20mm' }}>
          <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
            Agradecimentos
          </h1>
          {paragrafos(documento.agradecimentos).map((linha, indice) => (
            <p key={indice}>{comMarcas(linha)}</p>
          ))}
        </section>
      ) : null}

      {tem('EPIGRAFE') && documento.epigrafe?.trim() ? (
        <section data-folha-nova data-bloco="epigrafe" data-elemento="EPIGRAFE" style={{ marginBottom: '20mm', marginTop: '60mm', marginLeft: geometria.recuoCitacaoMm + 20 }}>
          <p style={{ textIndent: 0, fontStyle: 'italic' }}>{comMarcas(documento.epigrafe)}</p>
        </section>
      ) : null}

      {/* ---------------- APRESENTAÇÃO ---------------- */}
      {tem('APRESENTACAO') && documento.apresentacao?.trim() ? (
        <section data-folha-nova data-bloco="apresentacao" data-elemento="APRESENTACAO" style={{ marginBottom: '20mm' }}>
          <h1
            style={{
              fontSize: `${geometria.corpoPt}pt`,
              textTransform: 'uppercase',
              textAlign: 'center',
              marginBottom: `${ESPACO_APOS_TITULO_MM}mm`,
            }}
          >
            Apresentação
          </h1>
          {paragrafos(documento.apresentacao).map((linha, indice) => (
            <p key={indice}>{comMarcas(linha)}</p>
          ))}
        </section>
      ) : null}

      {/* ---------------- RESUMO / ABSTRACT ---------------- */}
      {tem('RESUMO') ? (
        <section data-folha-nova data-bloco="resumo" data-elemento="RESUMO" style={{ marginBottom: '20mm' }}>
          <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
            Resumo
          </h1>
          <p style={{ textIndent: 0, fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, textAlign: 'justify' }}>
            {comMarcas(resumoComPalavras(documento.resumo, documento.palavrasChave))}
          </p>
        </section>
      ) : null}

      {tem('ABSTRACT') ? (
        <section data-folha-nova data-bloco="abstract" data-elemento="ABSTRACT" style={{ marginBottom: '20mm' }}>
          <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
            Abstract
          </h1>
          <p style={{ textIndent: 0, fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, textAlign: 'justify' }}>
            {resumoComPalavras(documento.abstract, documento.keywords)}
          </p>
        </section>
      ) : null}

      {/* ---------------- RESUMOS EM OUTROS IDIOMAS ---------------- */}
      {(documento.resumos ?? [])
        .filter((item) => item.resumo.trim())
        .map((item, indice) => (
          <section
            key={`${item.titulo}-${indice}`}
            data-folha-nova
            data-bloco={`resumo-idioma-${indice}`}
            data-elemento="RESUMO_EM_IDIOMA"
            style={{ marginBottom: '20mm' }}
          >
            <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
              {item.titulo.trim() || 'Resumo'}
            </h1>
            <p style={{ textIndent: 0, fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, textAlign: 'justify' }}>
              {resumoComPalavras(item.resumo, item.palavrasChave)}
            </p>
          </section>
        ))}

      {/* ---------------- LISTAS ---------------- */}
      {tem('LISTA_DE_FIGURAS') && documento.figuras.length > 0 ? (
        <section data-folha-nova data-bloco="lista-figuras" data-elemento="LISTA_DE_FIGURAS" style={{ marginBottom: '20mm' }}>
          <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
            Lista de figuras
          </h1>
          {documento.figuras.map((figura, indice) => (
            <p key={figura.id} style={{ textIndent: 0 }}>
              Figura {mapaDasFiguras.get(figura.id) ?? indice + 1} — {comMarcas(figura.legenda)}
            </p>
          ))}
        </section>
      ) : null}

      {tem('LISTA_DE_ABREVIATURAS') && documento.abreviaturas.length > 0 ? (
        <section data-folha-nova data-bloco="lista-abreviaturas" data-elemento="LISTA_DE_ABREVIATURAS" style={{ marginBottom: '20mm' }}>
          <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
            Lista de abreviaturas e siglas
          </h1>
          {[...documento.abreviaturas].sort().map((linha, indice) => (
            <p key={indice} style={{ textIndent: 0 }}>{comMarcas(linha)}</p>
          ))}
        </section>
      ) : null}

      {/* ---------------- SUMÁRIO ---------------- */}
      {tem('SUMARIO') ? (
        <section data-folha-nova data-bloco="sumario" data-elemento="SUMARIO" style={{ marginBottom: '20mm' }}>
          <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
            Sumário
          </h1>
          {secoes.map((item) => {
            const destino = idDaSecao(item.secao.id);
            const numeroDaPagina = paginas?.get(item.secao.id);
            return (
              <p
                key={item.secao.id}
                style={{
                  textIndent: 0,
                  fontWeight: item.nivel === 1 ? 700 : 400,
                  marginLeft: `${(item.nivel - 1) * 6}mm`,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '2mm',
                }}
              >
                {/*
                 * A linha do sumário é **link**: clicar cai no título da seção, como
                 * no sumário do Word. O `href` é o mesmo alvo do clique (e é ele que
                 * o PDF do navegador transforma em link interno); o estilo não muda —
                 * o sumário impresso continua texto comum, sem azul nem sublinhado.
                 */}
                <a
                  href={`#${destino}`}
                  onClick={(evento) => irParaOSecao(evento, destino)}
                  style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                >
                  {item.numero ? `${item.numero} ` : ''}
                  {/*
                   * O título entra pelas mesmas marcas do texto (`comMarcas`): um
                   * capítulo com uma palavra em negrito tem de sair em negrito
                   * **também** no sumário. Sem isto, o `**` que o editor gravou
                   * aparecia impresso no meio do título — o defeito que só se vê na
                   * hora de imprimir.
                   */}
                  {comMarcas(item.titulo)}
                </a>
                {numeroDaPagina !== undefined ? (
                  <>
                    <span
                      aria-hidden="true"
                      style={{ flex: 1, minWidth: '6mm', borderBottom: '0.2mm dotted #999999', transform: 'translateY(-1mm)' }}
                    />
                    <span data-pagina-do-sumario="">{numeroDaPagina}</span>
                  </>
                ) : null}
              </p>
            );
          })}
        </section>
      ) : null}

      {/*
        ---------------- TÍTULO E AUTORIA (artigo) ----------------
        Vem **antes** do resumo, e a ordem é a da norma: o artigo abre pelo título e pela
        autoria, depois vêm o resumo com as palavras-chave e o abstract. Sem esta ordem o
        artigo começava pelo "Resumo:" e o título aparecia embaixo dele.
      */}
      {documento.modelo === 'ARTIGO_CIENTIFICO' ? (
        <header data-bloco="titulo-artigo" style={{ marginBottom: '10mm' }}>
          <p className="abnt-titulo" style={{ fontWeight: 700 }}>{tituloCompleto || 'TÍTULO DO ARTIGO'}</p>
          <p className="abnt-titulo" style={{ marginTop: '4mm' }}>
            {documento.identificacao.autores
              .map((autor) => [autor.nome, autor.instituicao].filter(Boolean).join(' — '))
              .filter(Boolean)
              .join('; ')}
          </p>
        </header>
      ) : null}

      {/* ---------------- RESUMO DO ARTIGO ---------------- */}
      {tem('RESUMO_DO_ARTIGO') ? (
        <section data-bloco="resumo-artigo" data-elemento="RESUMO_DO_ARTIGO" style={{ marginBottom: '10mm' }}>
          <p style={{ textIndent: 0, fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, textAlign: 'justify' }}>
            <strong>Resumo:</strong> {documento.resumo.trim() || '—'}
          </p>
          <p style={{ textIndent: 0, fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, marginTop: '2mm' }}>
            <strong>Palavras-chave:</strong> {documento.palavrasChave.join('; ') || '—'}.
          </p>
          <p style={{ textIndent: 0, fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, textAlign: 'justify', marginTop: '4mm' }}>
            <strong>Abstract:</strong> {documento.abstract.trim() || '—'}
          </p>
          <p style={{ textIndent: 0, fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1, marginTop: '2mm' }}>
            <strong>Keywords:</strong> {documento.keywords.join('; ') || '—'}.
          </p>
        </section>
      ) : null}

      {/* ---------------- TEXTO ---------------- */}
      {secoes.map((item, indice) => {
        const primaria = item.nivel === 1;
        /*
         * Quebra de folha: **só a seção primária** (o título 1) abre folha nova — é o
         * que a norma permite e o que o modelo pede (e não o artigo, que é texto
         * corrido). Subseção nunca começa em folha nova: ela corre com o texto, com uma
         * linha antes e uma depois. Uma subseção marcada à mão para abrir folha nova
         * (documento antigo) também sai corrida — a hierarquia da norma vale mais que
         * a marcação antiga, e o texto não fica com buraco branco no meio.
         */
        /* A conta da quebra de folha é feita uma vez, antes do desenho (ver acima). */
        const folhaNova = abreFolhaNova[indice]!;

        return (
          <Fragment key={item.secao.id}>
            {/*
              As folhas de anotação do capítulo que vem: entram **antes** dele, que é
              onde a anotação serve — ao lado do capítulo que está sendo lido. O
              primeiro capítulo não ganha folhas, porque não há nada antes dele.
            */}
            {folhasAntesDe(indice) ? (
              <FolhasDeAnotacao
                pauta={pauta}
                corpoPt={geometria.reduzidoPt}
                quantas={quantasAnotacoes}
                onde={item.secao.id}
                /* O capítulo seguinte já abre folha nova? Então não quebre duas vezes. */
                quebraDepois={!abreFolhaNova[indice]}
              />
            ) : null}

            <section
            id={idDaSecao(item.secao.id)}
            data-bloco={`secao-${item.numero}`}
            data-elemento="TEXTO"
            /* A letra capitulada é do capítulo: ela só entra na seção primária. */
            data-capitulo={primaria ? '' : undefined}
            {...(folhaNova ? { 'data-folha-nova': '' } : {})}
          >
            <AberturaDaSecao
              item={item}
              geometria={geometria}
              formato={formato}
              corpoDoTituloPt={corpoDoTituloPt}
              umaLinhaMm={umaLinhaMm}
              acoes={acoes}
              primaCaixaAltaPorNorma={documento.modelo !== 'LIVRO'}
            />

            <BlocosDaSecao
              secaoId={item.secao.id}
              tituloDaSecao={item.secao.titulo}
              blocos={blocosDaSecao(item.secao)}
              geometria={geometria}
              estiloDoParagrafo={estiloDoParagrafo}
              acoes={acoes}
              numerosDasNotas={mapaDasNotas}
              numerosDasFiguras={mapaDasFiguras}
              notasNoFimDaSecao={notasNoFimDaSecao}
              imagem={imagem}
              larguraDaManchaMm={larguraDaManchaMm}
              legendaAbaixo={legendaAbaixo}
              aoNovoBloco={aoNovoBloco}
              aoApagarBloco={aoApagarBloco}
            />
            </section>
          </Fragment>
        );
      })}

      {/* ---------------- FIGURAS (junto do texto) ---------------- */}
      {documento.figuras.length > 0
        ? documento.figuras.map((figura, indice) => {
            const desenho = figura.url ? (
              <img src={imagem(figura.url)} alt={figura.legenda} style={{ width: `${figura.larguraMm}mm`, maxWidth: '100%' }} />
            ) : (
              <div
                style={{
                  width: `${figura.larguraMm}mm`,
                  maxWidth: '100%',
                  height: '50mm',
                  margin: '0 auto',
                  border: '0.2mm solid #999',
                }}
              />
            );

            const rotulo = (
              <figcaption
                style={{
                  fontSize: `${geometria.reduzidoPt}pt`,
                  lineHeight: 1,
                  marginTop: legendaAbaixo ? '2mm' : 0,
                  marginBottom: legendaAbaixo ? 0 : '2mm',
                }}
              >
                Figura {mapaDasFiguras.get(figura.id) ?? indice + 1} — {comMarcas(figura.legenda)}
                {figura.fonte ? (
                  <>
                    <br />
                    Fonte: {comMarcas(figura.fonte)}
                  </>
                ) : null}
              </figcaption>
            );

            return (
              <figure key={figura.id} style={{ margin: '8mm 0', textAlign: 'center', breakInside: 'avoid' }}>
                {legendaAbaixo ? desenho : rotulo}
                {legendaAbaixo ? rotulo : desenho}
              </figure>
            );
          })
        : null}

      {/* ---------------- EPÍLOGO ---------------- */}
      {tem('EPILOGO') && documento.epilogo?.trim() ? (
        <section data-folha-nova data-bloco="epilogo" data-elemento="EPILOGO" style={{ marginBottom: '20mm' }}>
          <h1
            style={{
              fontSize: `${geometria.corpoPt}pt`,
              textTransform: 'uppercase',
              textAlign: 'center',
              marginBottom: `${ESPACO_APOS_TITULO_MM}mm`,
            }}
          >
            Epílogo
          </h1>
          {paragrafos(documento.epilogo).map((linha, indice) => (
            <p key={indice}>{comMarcas(linha)}</p>
          ))}
        </section>
      ) : null}

      {/* ---------------- SOBRE O AUTOR ---------------- */}
      {tem('SOBRE_O_AUTOR') && documento.sobreOAutor?.trim() ? (
        <section data-folha-nova data-bloco="sobre-o-autor" data-elemento="SOBRE_O_AUTOR" style={{ marginBottom: '20mm' }}>
          <h1
            style={{
              fontSize: `${geometria.corpoPt}pt`,
              textTransform: 'uppercase',
              textAlign: 'center',
              marginBottom: `${ESPACO_APOS_TITULO_MM}mm`,
            }}
          >
            Sobre o autor
          </h1>
          {paragrafos(documento.sobreOAutor).map((linha, indice) => (
            <p key={indice}>{comMarcas(linha)}</p>
          ))}
        </section>
      ) : null}

      {/* ---------------- NOTAS DE FIM (opcional) ---------------- */}
      {todasAsNotas.length > 0 ? (
        <>
          <h1
            style={{
              fontSize: `${geometria.corpoPt}pt`,
              textTransform: 'uppercase',
              textAlign: 'center',
              marginBottom: `${ESPACO_APOS_TITULO_MM}mm`,
            }}
          >
            Notas
          </h1>
          <ListaDeNotas itens={todasAsNotas} geometria={geometria} onde="documento" />
        </>
      ) : null}

      {/* ---------------- REFERÊNCIAS ---------------- */}
      {tem('REFERENCIAS') ? (
        <section
          /*
           * No artigo a lista **continua o texto**: o artigo é publicado como uma peça
           * corrida, e as referências entram logo depois da última seção. No trabalho e no
           * livro a lista abre folha nova, como a norma pede.
           */
          {...(documento.modelo === 'ARTIGO_CIENTIFICO' ? {} : { 'data-folha-nova': '' })}
          data-bloco="referencias"
          data-elemento="REFERENCIAS"
        >
          <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
            Referências
          </h1>
          {referencias.length === 0 ? (
            <p style={{ textIndent: 0 }}>Nenhuma referência cadastrada.</p>
          ) : (
            referencias.map((item) => (
              <p
                key={item.referencia.id}
                style={{ textIndent: 0, marginBottom: '6mm', fontSize: `${geometria.reduzidoPt}pt`, lineHeight: 1 }}
              >
                {comMarcas(linhaDaReferencia(item.referencia, norma, item.numero))}
              </p>
            ))
          )}
        </section>
      ) : null}

      {/* ---------------- APÊNDICES E ANEXOS ---------------- */}
      {(['APENDICES', 'ANEXOS'] as const)
        .filter((item) => tem(item))
        .map((item) => {
          const anexos = item === 'ANEXOS' ? (documento.anexos ?? []) : [];
          return (
            <section key={item} data-folha-nova data-bloco={item.toLowerCase()} data-elemento={item}>
              <h1 style={{ fontSize: `${geometria.corpoPt}pt`, textTransform: 'uppercase', textAlign: 'center', marginBottom: `${ESPACO_APOS_TITULO_MM}mm` }}>
                {item === 'APENDICES' ? 'Apêndices' : 'Anexos'}
              </h1>
              {anexos.length > 0 ? (
                anexos.map((anexo, indice) => (
                  <div key={anexo.id} style={{ breakBefore: 'page', marginBottom: '10mm' }}>
                    <h2 style={{ fontSize: `${geometria.corpoPt}pt`, fontWeight: 700, textAlign: 'center', marginBottom: '4mm' }}>
                      {`Anexo ${String.fromCharCode(65 + indice)}`}
                    </h2>
                    <p style={{ textIndent: 0, textAlign: 'center', marginBottom: '4mm' }}>{anexo.nome}</p>
                    <iframe
                      data-anexo-pdf=""
                      title={anexo.nome}
                      src={imagem(anexo.url)}
                      style={{ width: '100%', height: '80mm', border: '0.2mm solid #cccccc', background: '#ffffff' }}
                    />
                  </div>
                ))
              ) : (
                <p style={{ textIndent: 0 }}>
                  {item === 'APENDICES'
                    ? 'Material produzido por você: cole ou escreva aqui, com letra A (Apêndice A, Apêndice B...).'
                    : 'Material de terceiros que embasa o trabalho: envie os PDFs na aba Anexos, ou cole aqui com letra A (Anexo A, Anexo B...).'}
                </p>
              )}
            </section>
          );
        })}

      {/* ---------------- FOLHAS DE ANOTAÇÃO (no fim do documento) ---------------- */}
      {anotacoesNoFim ? (
        <FolhasDeAnotacao
          pauta={pauta}
          corpoPt={geometria.reduzidoPt}
          quantas={quantasAnotacoes}
          onde="fim"
        />
      ) : null}

      {/* Faixa do pé: o que sai embaixo de todas as folhas (na tela, a tira cinza). */}
      {temFaixa(moldura.comum.pe) ? (
        <div
          aria-hidden
          data-faixa-previa="pe"
          style={{ ...estiloDaFaixaEmCss(documento), borderTop: '0.2mm dashed #c9ccd4' }}
        >
          <span>{textoDaCaixa(moldura.comum.pe.esquerda)}</span>
          <span>{textoDaCaixa(moldura.comum.pe.centro)}</span>
          <span>{textoDaCaixa(moldura.comum.pe.direita)}</span>
        </div>
      ) : null}


    </div>
  );
}

/**
 * A abertura da seção — o "modelo do capítulo".
 *
 * O **destaque de cada nível** vem do motor (`destaqueDaSecao`): a seção primária
 * em maiúsculas e negrito, a secundária em negrito, a terciária em itálico — a
 * numeração progressiva da NBR 6024, que faz a hierarquia se ver na folha sem
 * contar os números.
 *
 * A seção **primária** obedece também ao formato escolhido: rótulo e título em
 * duas linhas centralizadas, como livro, ou na mesma linha à esquerda, como o
 * trabalho acadêmico.
 */
function AberturaDaSecao({
  item,
  geometria,
  formato,
  corpoDoTituloPt,
  umaLinhaMm,
  acoes,
  primaCaixaAltaPorNorma,
}: {
  item: SecaoNumerada;
  geometria: ReturnType<typeof geometriaDoPapel>;
  formato: ReturnType<typeof formatoDoCapitulo>;
  /** O corpo do título do capítulo, em pontos (com o tamanho que a pessoa escolheu). */
  corpoDoTituloPt: number;
  /** Uma linha do texto, em milímetros: é o que separa título e texto. */
  umaLinhaMm: number;
  acoes: AcoesDoTexto;
  /**
   * O modelo é dos que a norma rege (trabalho acadêmico, artigo): a seção primária
   * sai em maiúsculas **por norma**, e não por escolha. No livro, o capítulo em
   * caixa alta ou não é decisão de quem escreve — e um livro salvo com a escolha
   * antiga continua saindo como estava.
   */
  primaCaixaAltaPorNorma: boolean;
}) {
  /*
   * O título também se edita no papel: é texto do documento como qualquer outro —
   * o número ("1.", "2.1.") é que não, porque quem escreve esse é o motor, a partir
   * da ordem das seções.
   */
  const titulo = (estilo: CSSProperties, rotuloDoBloco: string, como: 'h2' | 'span' = 'h2') => (
    <TextoEditavel
      como={como}
      valor={item.titulo}
      rotulo={rotuloDoBloco}
      style={estilo}
      /* O título da seção sai marcado: é por ele que se acha a seção no papel. */
      dados={{ 'data-titulo-da-secao': '' }}
      {...acoesDoBloco({ qual: 'TITULO', secaoId: item.secao.id }, acoes)}
    />
  );

  const destaque = destaqueDaSecao(item.nivel);
  /** O título da seção primária, com a caixa alta que a norma pede ou a que a pessoa escolheu. */
  const tituloEmCaixaAlta = primaCaixaAltaPorNorma || formato.caixaAlta;

  if (item.nivel !== 1) {
    return (
      <h2
        style={{
          fontSize: `${geometria.corpoPt}pt`,
          /*
           * O negrito e o itálico vêm do destaque, e a folha de estilo do documento
           * já dá negrito a todo `h2`: por isso o itálico precisa **tirar** o negrito
           * da terceira seção, e não só acrescentar o itálico.
           */
          fontWeight: destaque.negrito ? 700 : 400,
          fontStyle: destaque.italico ? 'italic' : 'normal',
          /* Uma linha antes e uma depois — o "espaço de 1,5" da norma. */
          marginTop: `${umaLinhaMm}mm`,
          marginBottom: `${umaLinhaMm}mm`,
          marginLeft: `${(item.nivel - 1) * 6}mm`,
        }}
      >
        {/*
         * O número é do motor (1.1, 1.1.1): ele fica **fora** do bloco editável,
         * porque quem numera é a ordem das seções, e não quem escreve o título.
         */}
        {item.numero ? `${item.numero} ` : ''}
        {titulo({}, item.nivel === 2 ? 'Título da subseção' : 'Título da subparte', 'span')}
      </h2>
    );
  }

  const rotulo = rotuloDoCapitulo({ ordinal: item.ordinal, formato, padrao: item.numero });
  const centralizado = formato.alinhamento === 'CENTRO';

  return (
    <header
      style={{
        marginTop: `${formato.respiroAntesMm}mm`,
        /* Uma linha entre o título do capítulo e o texto que vem depois dele. */
        marginBottom: `${umaLinhaMm}mm`,
        textAlign: centralizado ? 'center' : 'left',
        /*
         * `avoid` mantém o título junto do primeiro parágrafo (sem título órfão
         * no pé da folha); com o título isolado, a quebra vem **depois** dele, e
         * o texto do capítulo começa na folha seguinte.
         */
        breakAfter: formato.tituloIsolado ? 'page' : 'avoid',
      }}
    >
      {centralizado ? (
        <>
          {rotulo ? (
            <p
              style={{
                textIndent: 0,
                textAlign: 'center',
                fontSize: `${geometria.reduzidoPt}pt`,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#333333',
              }}
            >
              {rotulo}
            </p>
          ) : null}
          {titulo(
            {
              fontSize: `${corpoDoTituloPt}pt`,
              marginTop: rotulo ? '4mm' : 0,
              textAlign: 'center',
              textTransform: tituloEmCaixaAlta ? 'uppercase' : 'none',
            },
            'Título do capítulo',
          )}
        </>
      ) : (
        <>
          {rotulo ? (
            <span
              style={{
                fontSize: `${corpoDoTituloPt}pt`,
                fontWeight: 700,
                textTransform: tituloEmCaixaAlta ? 'uppercase' : 'none',
              }}
            >
              {rotulo}{' '}
            </span>
          ) : null}
          {titulo(
            {
              fontSize: `${corpoDoTituloPt}pt`,
              textTransform: tituloEmCaixaAlta ? 'uppercase' : 'none',
              /* O título fica na mesma linha do rótulo, como um `h2` de verdade. */
              display: 'inline',
            },
            'Título do capítulo',
          )}
        </>
      )}
    </header>
  );
}

/**
 * A pauta da folha de anotação.
 *
 * Linhas e bolinhas saem de um fundo repetido — não de centenas de elementos —,
 * e o passo é o que o motor calculou para aquela mancha: é o mesmo número que
 * decide quantas linhas cabem, então o desenho e a conta não podem divergir.
 */
function fundoDaPauta(pauta: ReturnType<typeof pautaDaAnotacao>): string | undefined {
  if (pauta.formato === 'LINHAS') {
    return `repeating-linear-gradient(to bottom, #cfcfcf 0, #cfcfcf 0.18mm, transparent 0.18mm, transparent ${pauta.espacamentoMm}mm)`;
  }
  if (pauta.formato === 'PONTOS') {
    return 'radial-gradient(circle, #c9c9c9 0.22mm, transparent 0.3mm)';
  }
  return undefined;
}

/**
 * Um jogo de **folhas de anotação**.
 *
 * É um componente porque as folhas entram em mais de um lugar: no fim do documento
 * (todas juntas) ou **antes de cada capítulo** — que é onde elas servem, ao lado do
 * capítulo que está sendo lido.
 *
 * Três detalhes que o desenho cuida:
 *
 * 1. **a folha é a folha inteira** (`height` da pauta, `overflow: hidden`), e ela
 *    nunca invade a folha seguinte — a conta da altura é do motor (`pautaDaAnotacao`);
 * 2. **a quebra de folha é de quem vem depois**: a **primeira** folha do jogo abre
 *    folha nova; a **última** só quebra a página quando quem vem depois não abre
 *    folha sozinho (`quebraDepois`). Duas quebras seguidas sairiam como uma folha em
 *    branco no meio do trabalho;
 * 3. **a folha lisa não leva título**: é folha para usar, não para ler — e o rótulo
 *    "Anotações" ocuparia a primeira linha de quem for escrever.
 */
function FolhasDeAnotacao({
  pauta,
  corpoPt,
  quantas,
  onde,
  quebraDepois = false,
}: {
  pauta: ReturnType<typeof pautaDaAnotacao>;
  /** O corpo da letra do rótulo ("Anotações"), em pontos. */
  corpoPt: number;
  /** Quantas folhas neste jogo (o número escolhido no painel). */
  quantas: number;
  /** Um pedaço do `key`: as folhas podem aparecer em vários pontos do documento. */
  onde: string;
  /** A última folha fecha a página? (quando o que vem depois não abre folha nova) */
  quebraDepois?: boolean;
}) {
  return (
    <>
      {Array.from({ length: quantas }, (_, folha) => (
        <section
          key={`anotacao-${onde}-${folha}`}
          data-bloco="anotacao"
          data-elemento="PAGINA_DE_ANOTACAO"
          data-folha-inteira
          {...(folha === 0 ? { 'data-folha-nova': '' } : {})}
          style={{
            /* Altura exata da página de anotação: uma folha pautada nunca invade a próxima. */
            height: `${pauta.alturaMm}mm`,
            breakAfter: folha === quantas - 1 && !quebraDepois ? 'auto' : 'page',
          }}
        >
          {/* A folha lisa não leva título: é folha para usar, não para ler. */}
          {pauta.formato === 'BRANCA' ? null : (
            <h1
              style={{
                flex: '0 0 auto',
                height: `${pauta.respiroMm}mm`,
                margin: 0,
                textAlign: 'center',
                fontSize: `${corpoPt}pt`,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#333333',
              }}
            >
              Anotações
            </h1>
          )}
          <div
            style={{
              flex: '1 1 auto',
              backgroundImage: fundoDaPauta(pauta),
              backgroundSize:
                pauta.formato === 'PONTOS' ? `${pauta.espacamentoMm}mm ${pauta.espacamentoMm}mm` : undefined,
            }}
          />
        </section>
      ))}
    </>
  );
}

/** Alguma caixa desta faixa tem conteúdo? */
function temFaixa(faixa: { esquerda: CaixaDaFolha; centro: CaixaDaFolha; direita: CaixaDaFolha }): boolean {
  return [faixa.esquerda, faixa.centro, faixa.direita].some((caixa) => caixa.tipo !== 'VAZIA');
}

/** O que escrever na faixa da prévia: o texto, ou um aviso de que ali vai o número. */
function textoDaCaixa(caixa: CaixaDaFolha): string {
  if (caixa.tipo === 'TEXTO') return caixa.texto;
  if (caixa.tipo === 'NUMERO') return 'nº da folha';
  return '';
}

/** Quebra um texto de caixa em parágrafos (uma linha em branco separa). */
function paragrafos(texto: string): string[] {
  return texto
    .split(/\n{2,}/)
    .map((bloco) => bloco.trim())
    .filter(Boolean);
}

/**
 * O motor devolve as referências com `**negrito**` (o título da obra) — a mesma
 * marca do Markdown, e por isso quem desenha é `comMarcas`, junto com o texto do
 * autor. Não há dois caminhos para a mesma marca.
 */

/** Exportado para a prévia do editor usar a mesma numeração. */
export type { SecaoNumerada };
